import type { HttpEvent } from "@angular/common/http";
import { Component, DestroyRef, inject, output } from "@angular/core";
import { AlertService, FileUpload, type ProcessingStatus } from "@ui";
import { map, type Observable } from "rxjs";
import { DataSetType } from "@pienso/ingest-types/entity";
import { CorpusDataService } from "../../services/corpus-data.service";
import { LibraryDataService } from "../../services/data-library.service";
import { SourceDataService } from "../../services/source-data.service";

@Component({
  selector: "app-ingest-modal-upload",
  template: `
  <p-file-upload
    accept=".tsv,.csv,.json,.zip"
    [uploadFn]="uploadFn"
    [processingPollFn]="processingPollFn"
    [autoUpload]="true"
    helperText="or drag TSV, CSV, JSON or ZIP files"
    nextAction="Refine data"
    (nextActionClick)="onRefine($event)"
    (fileRemoved)="onRemoved($event)"
  />`,
  imports: [FileUpload],
})
export class IngestModalUploadComponent {
  private sourceDataService = inject(SourceDataService);
  private corpusService = inject(CorpusDataService);
  private libraryDataService = inject(LibraryDataService);
  private alertService = inject(AlertService).withDestroyRef(inject(DestroyRef));

  // emits the corpus id once the user chooses to refine an uploaded/refined file
  readonly selectedCorpusId = output<number>();

  // tracks what's been created for each file so the trash button can clean it all up
  private readonly entityIdsByFile = new Map<File, { sourceId: number; corpusId?: number }>();

  readonly uploadFn = (file: File): Observable<HttpEvent<unknown>> =>
    this.sourceDataService.uploadFile(file).pipe(map(({ event }) => event));

  readonly processingPollFn = (file: File, response: string): Observable<ProcessingStatus> => {
    this.entityIdsByFile.set(file, { sourceId: +response });
    return this.corpusService.refineSourceData(+response).pipe(
      map((corpusId) => {
        this.entityIdsByFile.get(file)!.corpusId = corpusId;
        return { status: "success" as const };
      }),
    );
  };

  onRefine(file: File) {
    const corpusId = this.entityIdsByFile.get(file)?.corpusId;
    if (corpusId != null) {
      this.selectedCorpusId.emit(corpusId);
    }
  }

  onRemoved(file: File) {
    const entityIds = this.entityIdsByFile.get(file);
    if (!entityIds) {
      return;
    }

    if (entityIds.corpusId != null) {
      this.libraryDataService
        .updateEntity({ id: entityIds.corpusId, type: DataSetType.CORPUS, hidden: true })
        .catch((e: Error) => this.alertService.error(e.message));
    }

    this.libraryDataService
      .updateEntity({ id: entityIds.sourceId, type: DataSetType.SOURCE, hidden: true })
      .catch((e: Error) => this.alertService.error(e.message));

    this.entityIdsByFile.delete(file);
  }
}
